import React from 'react';
import { usePageMeta } from '../hooks/usePageMeta';
import styles from './PrivacyPage.module.css';

const PrivacyPage = () => {
  usePageMeta({
    title: 'Privacy',
    path: '/privacy',
    description: 'DarkNews privacy policy — how we collect, use, and retain reader and copyright-request data.',
  });
  return (
    <div className={styles.privacyPage}>
      <div className="container">
        <div className={styles.pageHeader}>
          <h1>Privacy Policy</h1>
          <p className={styles.lastUpdated}>Last updated: August 17, 2026</p>
        </div>

        <div className={styles.content}>
          <section className={styles.section}>
            <h2>1. Introduction</h2>
            <p>
              DarkNews ("we," "our," or "us") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard 
              your information when you visit our website <a href="https://darknews.ai">https://darknews.ai</a> 
              (the "Site"). Please read this Privacy Policy carefully.
            </p>
          </section>

          <section className={styles.section}>
            <h2>2. Information We Collect</h2>
            
            <h3>2.1 Information You Provide</h3>
            <p>We may collect information you voluntarily provide to us, including:</p>
            <ul>
              <li>Contact information (name, email address) when you contact us or submit a copyright / feed-removal request</li>
              <li>Authentication information when you log in via OAuth providers (Google, GitHub)</li>
              <li>Search queries and preferences</li>
              <li>Feedback and communications with us</li>
            </ul>

            <h3>2.2 Automatically Collected Information</h3>
            <p>We automatically collect certain information when you visit our Site:</p>
            <ul>
              <li>IP address and location data</li>
              <li>Browser type and version</li>
              <li>Device information and operating system</li>
              <li>Pages visited and time spent on pages</li>
              <li>Referring website information</li>
              <li>Search queries and interactions</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>3. How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>
            <ul>
              <li>Provide and maintain our news aggregation service</li>
              <li>Personalize content and improve user experience</li>
              <li>Analyze site usage and performance</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Process copyright and feed-removal requests submitted on our Copyright / DMCA page</li>
              <li>Ensure site security and prevent fraud</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>4. Cookies and Tracking Technologies</h2>
            
            <h3>4.1 Cookies</h3>
            <p>
              We use cookies and similar tracking technologies to enhance your experience on our Site. 
              Cookies are small data files stored on your device that help us:
            </p>
            <ul>
              <li>Remember your preferences and settings</li>
              <li>Analyze site traffic and usage patterns</li>
              <li>Provide personalized content</li>
              <li>Improve site functionality</li>
            </ul>

            <h3>4.2 Third-Party Cookies</h3>
            <p>We use the following third-party services that may set cookies:</p>
            <ul>
              <li><strong>Google Analytics</strong> - For website analytics and performance monitoring</li>
              <li><strong>Google AdSense</strong> - For advertising services (when implemented)</li>
              <li><strong>OAuth Providers</strong> - For authentication services (Google, GitHub)</li>
            </ul>

            <h3>4.3 Cookie Management</h3>
            <p>
              You can control cookies through your browser settings. However, disabling cookies 
              may affect the functionality of our Site.
            </p>
          </section>

          <section className={styles.section}>
            <h2>5. Third-Party Services</h2>
            
            <h3>5.1 Google Analytics</h3>
            <p>
              We use Google Analytics to analyze site usage and performance. Google Analytics 
              collects information about your use of our Site and may use cookies. For more 
              information about Google's privacy practices, please visit 
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                Google's Privacy Policy
              </a>.
            </p>

            <h3>5.2 Google AdSense</h3>
            <p>
              We may use Google AdSense to display advertisements on our Site. AdSense uses 
              cookies to serve ads based on your visits to our Site and other sites on the 
              Internet. You may opt out of personalized advertising by visiting 
              <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">
                Google Ad Settings
              </a>.
            </p>

            <h3>5.3 OAuth Authentication</h3>
            <p>
              We use OAuth providers (Google, GitHub) for user authentication. These services 
              may collect and process information according to their respective privacy policies.
            </p>
          </section>

          <section className={styles.section}>
            <h2>6. Data Sharing and Disclosure</h2>
            <p>We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:</p>
            <ul>
              <li>With your explicit consent</li>
              <li>To comply with legal obligations or court orders</li>
              <li>To protect our rights, property, or safety</li>
              <li>With service providers who assist in operating our Site (under strict confidentiality agreements)</li>
              <li>In connection with a business transfer or acquisition</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>7. Data Security</h2>
            <p>
              We implement appropriate technical and organizational security measures to protect 
              your personal information against unauthorized access, alteration, disclosure, or 
              destruction. However, no method of transmission over the Internet or electronic 
              storage is 100% secure.
            </p>
          </section>

          <section className={styles.section}>
            <h2>8. Data Retention</h2>
            <p>
              We retain your personal information only for as long as necessary to fulfill the 
              purposes outlined in this Privacy Policy, unless a longer retention period is 
              required or permitted by law.
            </p>
          </section>

          <section className={styles.section}>
            <h2>9. Your Rights and Choices</h2>
            <p>Depending on your location, you may have the following rights regarding your personal information:</p>
            <ul>
              <li>Access to your personal information</li>
              <li>Correction of inaccurate information</li>
              <li>Deletion of your personal information</li>
              <li>Restriction of processing</li>
              <li>Data portability</li>
              <li>Objection to processing</li>
            </ul>
            <p>
              To exercise these rights, please contact us using the information provided in 
              the Contact section below.
            </p>
          </section>

          <section className={styles.section}>
            <h2>10. Children's Privacy</h2>
            <p>
              Our Site is not intended for children under 13 years of age. We do not knowingly 
              collect personal information from children under 13. If you are a parent or guardian 
              and believe your child has provided us with personal information, please contact us.
            </p>
          </section>

          <section className={styles.section}>
            <h2>11. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your 
              country of residence. We ensure appropriate safeguards are in place to protect 
              your information in accordance with this Privacy Policy.
            </p>
          </section>

          <section className={styles.section}>
            <h2>12. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any 
              changes by posting the new Privacy Policy on this page and updating the "Last updated" 
              date. Your continued use of our Site after any modifications constitutes acceptance 
              of the updated Privacy Policy.
            </p>
          </section>

          <section className={styles.section}>
            <h2>13. Contact Information</h2>
            <p>
              If you have any questions about this Privacy Policy or our privacy practices, 
              please contact us:
            </p>
            <div className={styles.contactInfo}>
              <p><strong>Copyright / DMCA:</strong> <a href="/copyright">darknews.ai/copyright</a></p>
              <p><strong>Email:</strong> <a href="mailto:privacy@darknews.ai">privacy@darknews.ai</a></p>
              <p><strong>Website:</strong> <a href="https://darknews.ai">https://darknews.ai</a></p>
              <p><strong>Contact Page:</strong> <a href="/contact">Contact Us</a></p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
